import { GripVertical } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent } from "react";
import { categoryName } from "../lib/category-icons.ts";
import type { PoiCategory } from "../lib/pois.ts";

/** The key under which places with no category are counted. */
export const NO_CATEGORY = "none";

export type CategoryCount = { places: number; visited: number };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * A trip's place categories as tall rows, in the order the trip keeps them, which anyone on the
 * trip can change: drag a row by its grip, or focus the grip and press the up or down arrow.
 *
 * Each row is two siblings: the grip, and a button that carries the face, the name and the count
 * and opens that category's editor (a button inside a button is not HTML).
 *
 * A reorder shows at once and is sent as the complete new order, which is what the server insists
 * on. If the server refuses, the list goes back to the last order it confirmed and says so. Quick
 * successive moves are sent one request at a time, the later ones coalesced, so a refusal always
 * has one confirmed order to return to.
 *
 * "No category" is not a category: it is counted like one but has no grip and nothing to edit, so
 * it is not a button, and it stays last. It is drawn only when some place has no category.
 *
 * This is only the list. The host owns the categories and performs the save (`onReorder`); where
 * the list stands, and what a tap on a row does to the map, are the host's too.
 */
export function CategoryList({
  categories,
  counts,
  onReorder,
  onEdit,
}: {
  /** In the order the trip keeps them. */
  categories: PoiCategory[];
  /** By category id, and `NO_CATEGORY` for places with none; a missing key is none. */
  counts: Readonly<Record<string, CategoryCount | undefined>>;
  /**
   * Performs the change: receives the id of every category in the new sequence (the server wants
   * them all) and resolves when it is saved, rejects when it is refused.
   */
  onReorder: (ids: string[]) => Promise<void>;
  /** Opens one category's editor. */
  onEdit: (id: string) => void;
}) {
  // What is on screen while a reorder is being made or saved; null follows `categories`.
  const [local, setLocal] = useState<PoiCategory[] | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [said, setSaid] = useState("");
  const [error, setError] = useState<string | null>(null);

  const shown = local ?? categories;
  const current = useRef(shown); // `shown`, but already up to date between two renders
  const confirmed = useRef(categories); // the last order the server is known to hold
  const saving = useRef(false);
  const dirty = useRef(false);
  const drag = useRef<{ id: string; from: PoiCategory[] } | null>(null);
  const focusAfter = useRef<string | null>(null);
  const list = useRef<HTMLUListElement>(null);

  useEffect(() => {
    // The host's list is the truth again once nothing of ours is pending.
    if (saving.current || drag.current) return;
    current.current = categories;
    confirmed.current = categories;
    setLocal(null);
  }, [categories]);

  useEffect(() => {
    // Moving a row in the DOM can drop focus off its grip; give it back.
    const id = focusAfter.current;
    if (id === null) return;
    focusAfter.current = null;
    list.current?.querySelector<HTMLElement>(`[data-grip="${CSS.escape(id)}"]`)?.focus();
  });

  function show(next: PoiCategory[]) {
    current.current = next;
    setLocal(next);
  }

  async function save() {
    if (saving.current) {
      dirty.current = true;
      return;
    }
    saving.current = true;
    try {
      do {
        dirty.current = false;
        const sent = current.current;
        await onReorder(sent.map((c) => c.id));
        confirmed.current = sent;
      } while (dirty.current);
    } catch {
      dirty.current = false;
      show(confirmed.current);
      setError("Could not save the new order. The list is back as it was.");
      setSaid("Could not save the new order. The list is back as it was.");
    } finally {
      saving.current = false;
    }
  }

  function moved(id: string, next: PoiCategory[]) {
    setError(null);
    show(next);
    const at = next.findIndex((c) => c.id === id);
    const name = categoryName(next[at]?.name ?? "");
    setSaid(`${name} moved to position ${at + 1} of ${next.length}.`);
  }

  function onKey(e: KeyboardEvent, id: string) {
    const step = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const from = current.current;
    const at = from.findIndex((c) => c.id === id);
    const to = at + step;
    if (at < 0 || to < 0 || to >= from.length) return;
    const next = [...from];
    next.splice(to, 0, next.splice(at, 1)[0]!);
    focusAfter.current = id;
    moved(id, next);
    void save();
  }

  function onDown(e: PointerEvent<HTMLElement>, id: string) {
    if (e.button !== undefined && e.button > 0) return;
    drag.current = { id, from: current.current };
    setDragging(id);
    // Keep the move and the drop coming to the grip when the finger leaves it.
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  function onMove(e: PointerEvent<HTMLElement>) {
    const d = drag.current;
    if (!d || !list.current) return;
    // Where the row would stand: after every other category whose middle the pointer has passed.
    const others = [...list.current.querySelectorAll<HTMLElement>("li[data-category-row]")].filter(
      (li) => li.dataset.categoryRow !== d.id && li.dataset.categoryRow !== NO_CATEGORY,
    );
    const to = others.filter((li) => {
      const r = li.getBoundingClientRect();
      return e.clientY > r.top + r.height / 2;
    }).length;
    const rest = current.current.filter((c) => c.id !== d.id);
    const row = current.current.find((c) => c.id === d.id);
    if (!row) return;
    const next = [...rest.slice(0, to), row, ...rest.slice(to)];
    if (next.every((c, i) => c === current.current[i])) return;
    moved(d.id, next);
  }

  function onUp(e: PointerEvent<HTMLElement>) {
    const d = drag.current;
    drag.current = null;
    setDragging(null);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (!d) return;
    if (current.current.every((c, i) => c.id === d.from[i]?.id)) return;
    void save();
  }

  function onCancel() {
    const d = drag.current;
    drag.current = null;
    setDragging(null);
    if (d) show(d.from);
  }

  const none = counts[NO_CATEGORY];
  return (
    <>
      <ul className="category-list" ref={list}>
        {shown.map((c) => {
          const n = counts[c.id];
          return (
            <li
              key={c.id}
              className="category-row"
              data-category-row={c.id}
              data-dragging={dragging === c.id || undefined}
              style={{ "--chip-color": c.color } as CSSProperties}
            >
              <button
                type="button"
                className="category-grip"
                data-grip={c.id}
                aria-label={`Reorder ${categoryName(c.name)}`}
                title={`Reorder ${categoryName(c.name)}: drag, or press the up and down arrows`}
                aria-describedby="category-grip-hint"
                onKeyDown={(e) => onKey(e, c.id)}
                onPointerDown={(e) => onDown(e, c.id)}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onCancel}
              >
                <GripVertical size={20} strokeWidth={2} aria-hidden="true" />
              </button>
              <Row
                face={c.emoji}
                name={categoryName(c.name)}
                count={n}
                label={`Edit ${categoryName(c.name)}, ${countSaid(n)}`}
                onClick={() => onEdit(c.id)}
              />
            </li>
          );
        })}
        {none && none.places > 0 && (
          <li className="category-row category-row-none" data-category-row={NO_CATEGORY}>
            <Row face="📍" name="No category" count={none} />
          </li>
        )}
      </ul>
      <p id="category-grip-hint" hidden>
        Press the up or down arrow to move this category.
      </p>
      <p className="sr" role="status">
        {said}
      </p>
      {error !== null && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

function countSaid(count: CategoryCount | undefined): string {
  return `${plural(count?.places ?? 0, "place", "places")}, ${count?.visited ?? 0} visited`;
}

/** The face, the name and the count; a button when there is something to open, else plain. */
function Row({
  face,
  name,
  count,
  label,
  onClick,
}: {
  face: string;
  name: string;
  count: CategoryCount | undefined;
  label?: string;
  onClick?: () => void;
}) {
  const inside = (
    <>
      <span className="category-face" aria-hidden="true">
        {face}
      </span>
      <span className="category-name">{name}</span>
      <span className="category-count small">{countSaid(count)}</span>
    </>
  );
  return onClick ? (
    <button type="button" className="category-open" aria-label={label} onClick={onClick}>
      {inside}
    </button>
  ) : (
    <div className="category-open">{inside}</div>
  );
}
